import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Button, IconButton, Grid, Chip,
  CircularProgress, Divider, Accordion, AccordionSummary,
  AccordionDetails, Alert, Tabs, Tab, LinearProgress, Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import useToetsNavigatie from '../../hooks/useToetsNavigatie';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TranscriptIcon from '@mui/icons-material/Subtitles';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import BugReportIcon from '@mui/icons-material/BugReport';
import { AI_ENGINE_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { bevestig } from '../../utils/bevestig';
import { useEigenThumb } from './eigenThumb';

const API_BASE = `${AI_ENGINE_URL}/api/v1/video-search`;

const formatTime = (seconds) => {
  if (!seconds && seconds !== 0) return '';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const extractTags = (description) => {
  if (!description) return [];
  const tags = [];
  const patterns = [
    { re: /\*\*Locatie:\*\*\s*([^.\n*]+)/i, prefix: '' },
    { re: /\*\*Sfeer:\*\*\s*([^.\n*]+)/i, prefix: '' },
    { re: /\*\*Tekst op scherm:\*\*\s*"?([^".\n*]+)"?/i, prefix: '' },
    { re: /\*\*Personen:\*\*\s*([^.\n*]+)/i, prefix: '' },
    { re: /\*\*Objecten:\*\*\s*([^.\n*]+)/i, prefix: '' },
  ];
  for (const { re, prefix } of patterns) {
    const m = description.match(re);
    if (m) {
      let val = m[1].trim().replace(/\*\*/g, '').replace(/\s+/g, ' ');
      if (val.length > 40) val = val.substring(0, 37) + '...';
      tags.push(prefix + val);
      if (tags.length >= 3) break;
    }
  }
  if (tags.length === 0) {
    const clean = description.replace(/\*\*/g, '').replace(/Frame \d+:/g, '').trim();
    const first = clean.split(/[.\n]/).find(s => s.trim().length > 5);
    if (first) tags.push(first.trim().substring(0, 40));
  }
  return tags;
};

// -- QA finding display config ------------------------------------------------

const FINDING_TYPE_META = {
  duplicate_restart: { label: 'Dubbele herstart', color: '#ef4444', icon: '🔁' },
  false_start: { label: 'Valse start', color: '#f59e0b', icon: '⏭' },
  long_silence: { label: 'Lange stilte', color: '#6366f1', icon: '🔇' },
  off_camera_direction: { label: 'Productie-instructie', color: '#ef4444', icon: '🎬' },
  background_voice: { label: 'Achtergrondstem', color: '#f97316', icon: '🗣' },
  stumble_or_repeat: { label: 'Hapering', color: '#8b5cf6', icon: '💬' },
};

const SEVERITY_META = {
  high: { label: 'Hoog', color: '#ef4444', bg: '#fef2f2' },
  medium: { label: 'Medium', color: '#f59e0b', bg: '#fffbeb' },
  low: { label: 'Laag', color: '#6b7280', bg: '#f9fafb' },
};

const ACTION_LABELS = {
  cut_segment: 'Knippen',
  review_manually: 'Handmatig checken',
  replace_with_b_roll: 'B-roll eroverheen',
  show_slide_instead: 'Slide gebruiken',
  keep_but_check: 'Behouden, maar checken',
  not_an_issue: 'Geen probleem',
};

// -- QA Tab -------------------------------------------------------------------

function QATab({ videoId }) {
  const [qaResult, setQaResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const runQA = useCallback(async () => {
    if (!(await bevestig({ titel: 'QA-check draaien?', tekst: 'Dit gebruikt AI-tegoed.', knop: 'Draaien' }))) return;
    setLoading(true);
    setError(null);
    setQaResult(null);
    try {
      const resp = await authFetch(`${API_BASE}/qa/${videoId}/run`, { method: 'POST' });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${resp.status}`);
      }
      setQaResult(await resp.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [videoId]);

  if (!qaResult && !loading && !error) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <BugReportIcon sx={{ fontSize: 48, color: '#6366f1', mb: 1.5, opacity: 0.6 }} />
        <Typography variant="body1" fontWeight={600} gutterBottom>
          Edit QA Check
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 400, mx: 'auto' }}>
          Analyseer het transcript op editfouten: dubbele zinnen, valse starts, productieresten, stiltes en haperingen.
        </Typography>
        <Button variant="contained" startIcon={<BugReportIcon />} onClick={runQA}
          sx={{ textTransform: 'none', bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' } }}>
          Start QA check
        </Button>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CircularProgress size={32} sx={{ mb: 2 }} />
        <Typography variant="body2" color="text.secondary">
          Transcript analyseren op editfouten...
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          Heuristieken + LLM evaluatie, dit kan 10-30 seconden duren
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 2 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button size="small" onClick={runQA} sx={{ textTransform: 'none' }}>Opnieuw proberen</Button>
      </Box>
    );
  }

  const { findings, summary } = qaResult;

  if (findings.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" sx={{ color: '#10b981', mb: 0.5 }}>Geen editfouten gevonden</Typography>
        <Typography variant="body2" color="text.secondary">
          Het transcript bevat geen duidelijke herstarts, stiltes of productieresten.
        </Typography>
        <Button size="small" onClick={runQA} sx={{ mt: 2, textTransform: 'none' }}>Opnieuw checken</Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Chip label={`${summary.total} finding${summary.total !== 1 ? 's' : ''}`} size="small" sx={{ fontWeight: 700 }} />
        {summary.by_severity?.high > 0 && (
          <Chip label={`${summary.by_severity.high} hoog`} size="small" sx={{ bgcolor: '#fef2f2', color: '#ef4444', fontWeight: 600 }} />
        )}
        {summary.by_severity?.medium > 0 && (
          <Chip label={`${summary.by_severity.medium} medium`} size="small" sx={{ bgcolor: '#fffbeb', color: '#f59e0b', fontWeight: 600 }} />
        )}
        {summary.by_severity?.low > 0 && (
          <Chip label={`${summary.by_severity.low} laag`} size="small" sx={{ bgcolor: '#f9fafb', color: '#6b7280' }} />
        )}
        <Box sx={{ flex: 1 }} />
        <Button size="small" onClick={runQA} sx={{ textTransform: 'none', fontSize: '0.75rem' }}>Opnieuw checken</Button>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {findings.map((f, i) => {
          const typeMeta = FINDING_TYPE_META[f.type] || { label: f.type, color: '#6b7280', icon: '?' };
          const sevMeta = SEVERITY_META[f.severity] || SEVERITY_META.medium;
          return (
            <Box key={i} sx={{
              border: 1, borderColor: 'divider', borderRadius: 1.5,
              borderLeft: `4px solid ${typeMeta.color}`, p: 1.5, '&:hover': { bgcolor: '#fafafa' },
            }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
                <Typography sx={{ fontSize: '1rem', lineHeight: 1 }}>{typeMeta.icon}</Typography>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="body2" fontWeight={700}>{typeMeta.label}</Typography>
                    <Chip label={f.timestamp_label} size="small" color="primary" variant="outlined"
                      sx={{ fontSize: '0.65rem', height: 20, fontFamily: 'monospace' }} />
                    <Chip label={sevMeta.label} size="small"
                      sx={{ fontSize: '0.6rem', height: 18, bgcolor: sevMeta.bg, color: sevMeta.color, fontWeight: 600 }} />
                    {f.confidence >= 0.7 && (
                      <Tooltip title={`Zekerheid: ${Math.round(f.confidence * 100)}%`}>
                        <LinearProgress variant="determinate" value={f.confidence * 100}
                          sx={{ width: 40, height: 4, borderRadius: 2, bgcolor: '#e5e7eb',
                            '& .MuiLinearProgress-bar': { bgcolor: f.confidence > 0.8 ? '#10b981' : '#f59e0b' } }} />
                      </Tooltip>
                    )}
                  </Box>
                  {f.reason && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, lineHeight: 1.4 }}>
                      {f.reason}
                    </Typography>
                  )}
                  {f.evidence_text && (
                    <Typography variant="caption" sx={{
                      display: 'block', bgcolor: '#f8fafc', borderRadius: 1,
                      px: 1, py: 0.5, fontFamily: 'monospace', fontSize: '0.7rem',
                      color: 'text.secondary', lineHeight: 1.5,
                    }}>
                      {f.evidence_text}
                    </Typography>
                  )}
                </Box>
                {f.suggested_action && f.suggested_action !== 'not_an_issue' && (
                  <Chip label={ACTION_LABELS[f.suggested_action] || f.suggested_action} size="small"
                    sx={{ fontSize: '0.6rem', height: 20, bgcolor: '#e0e7ff', color: '#4338ca' }} />
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

// -- Main Dialog --------------------------------------------------------------

export default function VideoDetailDialog({ open, onClose, videoId, onVorige, onVolgende, positie, zoekterm = '' }) {
  const spelerRef = useRef(null);
  const [treffer, setTreffer] = useState(null); // { timestamp, timestamp_formatted, snippet }
  useToetsNavigatie({ actief: open, onVorige, onVolgende });
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState(0);
  const poster = useEigenThumb(detail?.video_id ?? null, detail?.thumb_large || detail?.thumb_small, detail?.thumb_maken);

  useEffect(() => {
    if (open && videoId) {
      setDetail(null);
      setError(null);
      setTab(0);
      setTreffer(null);
      fetchDetail();
    }
  }, [open, videoId]);

  // Waar in de video zit de zoekterm? Eén verzoek, alleen als er gezocht is.
  useEffect(() => {
    const term = (zoekterm || '').trim();
    if (!open || !videoId || !term) return undefined;
    let actueel = true;
    (async () => {
      try {
        const r = await authFetch(`${API_BASE}/snippet/${videoId}?q=${encodeURIComponent(term)}`);
        if (!r.ok) return;
        const d = await r.json();
        if (actueel && d.found) setTreffer(d);
      } catch { /* geen tijdcode is geen fout */ }
    })();
    return () => { actueel = false; };
  }, [open, videoId, zoekterm]);

  const springNaarTreffer = () => {
    const el = spelerRef.current;
    if (el && treffer) { el.currentTime = Math.max(0, treffer.timestamp - 1); el.play?.().catch?.(() => {}); }
    else if (treffer?.player_url) window.open(treffer.player_url, '_blank');
  };

  const fetchDetail = async () => {
    setLoading(true);
    try {
      const resp = await authFetch(`${API_BASE}/detail/${videoId}`);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      setDetail(await resp.json());
    } catch (e) {
      setError('Laden mislukt: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  const hasTranscript = detail?.transcript_raw && detail.transcript_raw.length > 50;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box sx={{ overflow: 'hidden' }}>
          <Typography variant="h6" noWrap sx={{ fontWeight: 600 }}>
            {detail?.filename || 'Video analyse'}
          </Typography>
          {detail && (
            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
              {detail.client_name && <Chip label={detail.client_name} size="small" variant="outlined" />}
              {detail.project_number && <Chip label={`#${detail.project_number}`} size="small" color="primary" />}
              {detail.duration && <Chip icon={<AccessTimeIcon />} label={formatTime(detail.duration)} size="small" />}
              {detail.transcript_language && <Chip label={detail.transcript_language.toUpperCase()} size="small" />}
            </Box>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
        {(onVorige || onVolgende || positie) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, mr: 1 }}>
            <Tooltip title="Vorige (←)"><span>
              <IconButton size="small" disabled={!onVorige} onClick={onVorige} aria-label="Vorige"><ChevronLeftIcon /></IconButton>
            </span></Tooltip>
            {positie && (
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 56, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
                {positie.nr} / {positie.van}
              </Typography>
            )}
            <Tooltip title="Volgende (→)"><span>
              <IconButton size="small" disabled={!onVolgende} onClick={onVolgende} aria-label="Volgende"><ChevronRightIcon /></IconButton>
            </span></Tooltip>
          </Box>
        )}
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        )}
        {error && <Typography color="error" sx={{ py: 2 }}>{error}</Typography>}

        {detail && !loading && (
          <>
            {/* Video Player */}
            {detail.video_blob_url && (
              <Box sx={{ mb: 2 }}>
                <Box component="video" controls preload="metadata" ref={spelerRef}
                  poster={poster || ''}
                  sx={{ width: '100%', maxHeight: 400, borderRadius: 1, bgcolor: '#000', display: 'block' }}>
                  <source src={detail.video_blob_url} type="video/mp4" />
                </Box>
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip icon={<CloudDoneIcon />}
                    label={`Gearchiveerd${detail.video_blob_size_mb ? ` (${detail.video_blob_size_mb} MB)` : ''}`}
                    size="small" color="success" variant="outlined" />
                </Box>
              </Box>
            )}

            {treffer && (
              <Alert severity="success" icon={<AccessTimeIcon fontSize="inherit" />} sx={{ mb: 2 }}
                action={(
                  <Button size="small" color="inherit" onClick={springNaarTreffer} sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>
                    {detail.video_blob_url ? `Spring naar ${treffer.timestamp_formatted}` : `Open op ${treffer.timestamp_formatted}`}
                  </Button>
                )}>
                <Typography variant="body2">
                  Treffer op <strong>{treffer.timestamp_formatted}</strong>: „{treffer.snippet}"
                </Typography>
              </Alert>
            )}

            {/* Tabs */}
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Tab label="Analyse" sx={{ textTransform: 'none', fontWeight: 600 }} />
              {hasTranscript && (
                <Tab label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <BugReportIcon sx={{ fontSize: 16 }} /> Edit QA
                </Box>} sx={{ textTransform: 'none', fontWeight: 600 }} />
              )}
            </Tabs>

            {/* Tab 0: Analyse */}
            {tab === 0 && (
              <>
                {detail.keyframes && detail.keyframes.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <VisibilityIcon fontSize="small" /> Visuele analyse ({detail.keyframes.length} keyframes)
                    </Typography>
                    <Grid container spacing={1.5}>
                      {detail.keyframes.map((kf, idx) => (
                        <Grid item xs={12} sm={6} md={4} key={idx}>
                          <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden', height: '100%' }}>
                            {kf.blob_url ? (
                              <Box component="img" src={kf.blob_url} alt={`Frame ${formatTime(kf.time)}`}
                                sx={{ width: '100%', height: 160, objectFit: 'cover', display: 'block' }}
                                onError={(e) => { e.target.style.display = 'none'; }} />
                            ) : (
                              <Box sx={{ width: '100%', height: 160, bgcolor: 'grey.100', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Typography variant="caption" color="text.secondary">Geen afbeelding</Typography>
                              </Box>
                            )}
                            <Box sx={{ p: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                              <Chip label={formatTime(kf.time)} size="small" color="primary" variant="outlined" />
                              {extractTags(kf.description).map((tag, ti) => (
                                <Chip key={ti} label={tag} size="small" sx={{ fontSize: '0.65rem', height: 22, maxWidth: '100%' }} />
                              ))}
                            </Box>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}

                {(!detail.keyframes || detail.keyframes.length === 0) && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Geen visuele analyse beschikbaar.
                  </Typography>
                )}

                <Divider sx={{ my: 2 }} />

                {detail.transcript_raw ? (
                  <Accordion defaultExpanded={!detail.keyframes?.length}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <TranscriptIcon fontSize="small" /> Transcript
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2"
                        sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, color: 'text.secondary', fontSize: '0.8rem' }}>
                        {detail.transcript_raw}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Geen transcript beschikbaar (video bevat waarschijnlijk geen spraak).
                  </Typography>
                )}

                {(detail.audio_cost_usd > 0 || detail.visual_cost_usd > 0) && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                    API kosten: ${((detail.audio_cost_usd || 0) + (detail.visual_cost_usd || 0)).toFixed(4)}
                    {detail.audio_cost_usd > 0 && ` (audio: $${detail.audio_cost_usd.toFixed(4)})`}
                    {detail.visual_cost_usd > 0 && ` (visueel: $${detail.visual_cost_usd.toFixed(4)})`}
                  </Typography>
                )}
              </>
            )}

            {/* Tab 1: Edit QA */}
            {tab === 1 && hasTranscript && <QATab videoId={videoId} />}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} color="inherit">Sluiten</Button>
        {detail?.player_url && (
          <Button variant="contained" startIcon={<PlayArrowIcon />}
            onClick={() => window.open(detail.player_url, '_blank')}>
            Open in Frame.io
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
